<script lang="ts">
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePrizesStore } from '@/stores/prizes'
import type { InstantWinDraw, InstantWinMainPrize, Nullable } from '@/types'
import { type PropType, defineComponent } from 'vue'

import BaseCard from './new/BaseCard.vue'

/**
 * A card to display a draw.
 *
 */
export default defineComponent({
  components: {
    BaseCard,
  },
  props: {
    prize: {
      type: Object as PropType<InstantWinMainPrize>,
      required: true,
    },
  },
  setup() {
    return {
      prizesStore: usePrizesStore(),
    }
  },
  data() {
    return {
      draw: null as Nullable<InstantWinDraw>,
      isLoading: true,
    }
  },
  computed: {
    penceTicketCost() {
      return Math.round((this.draw?.ticket_cost || 0) * 100)
    },
  },
  async mounted() {
    const instantWinsStore = useInstantWinsStore()

    try {
      this.draw = await instantWinsStore.getInstantWin(this.prize.code)
    } finally {
      this.isLoading = false
    }
  },
})
</script>

<template>
  <!-- Skeleton while loading -->
  <div v-if="isLoading" class="h-full">
    <BaseCard class="flex h-full flex-col gap-0 p-0">
      <div class="h-12 animate-pulse bg-slate-300"></div>
      <div class="h-60 animate-pulse bg-slate-200"></div>
      <div class="h-16 animate-pulse bg-slate-300"></div>
      <div class="flex-grow p-5">
        <div class="mb-2 h-6 animate-pulse rounded bg-slate-200"></div>
        <div class="mb-4 h-4 animate-pulse rounded bg-slate-200"></div>
        <div class="mt-auto h-10 animate-pulse rounded bg-slate-200"></div>
      </div>
    </BaseCard>
  </div>

  <!-- Actual card when loaded -->
  <RouterLink
    v-else-if="draw"
    :to="{
      name: 'instant-win',
      params: { instantWinCode: draw.code },
    }"
  >
    <BaseCard class="flex h-full flex-col gap-0 p-0">
      <p
        class="flex items-center justify-center bg-tertiary px-2 py-3 font-semibold uppercase text-pure-white md:px-4"
      >
        <AppIcon :icon="Icons.Lightening" class="mr-2" />
        Win Right Now!
      </p>

      <div class="relative">
        <img
          :src="draw.main_prize.card_image.src"
          :alt="draw.main_prize.card_image.alt"
          width="340"
          height="240"
        />

        <div
          class="absolute right-2 top-2 rounded-100 bg-secondary px-4 py-1 font-bold"
        >
          {{ penceTicketCost }}p tickets
        </div>
      </div>

      <AppCountdown
        :datetime="prize.close_at"
        :show-full-units="true"
        class="w-full bg-black px-1 py-3 text-pure-white"
        time-class="text-xl font-extrabold leading-none"
        unit-class="text-xs font-semibold uppercase leading-none"
      />

      <div class="flex flex-grow flex-col justify-between gap-2 px-5 py-6">
        <span>
          <p class="text-lg font-extrabold">
            {{ draw.title }}
          </p>

          <p class="h-line-2 line-clamp-2" :title="draw.description">
            {{ draw.description }}
          </p>
        </span>

        <AppButton
          :route="{
            name: 'instant-win',
            params: { instantWinCode: draw.code },
          }"
          class="button-secondary button-secondary-light not:last:mb-2 mt-4 min-w-full font-semibold"
          title="Play Now"
          :icon="Icons.ArrowRight"
        >
          Play Now
        </AppButton>
      </div>
    </BaseCard>
  </RouterLink>
</template>
